import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './AnalisisCostes.css';

const AnalisisCostes = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTypes, setExpandedTypes] = useState(new Set());
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [areaFiltro, setAreaFiltro] = useState('');

  useEffect(() => {
    fetchAnalisisCostes();
  }, []);

  const fetchAnalisisCostes = async (estado = '', area = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (estado) {
        params.append('estado', estado);
      }
      if (area) {
        params.append('area_tecnica', area);
      }
      const response = await axios.get(`/api/iniciativas/analisis_costes/?${params}`);
      setData(response.data);
    } catch (err) {
      setError('Error al cargar los datos de análisis de costes');
      console.error('Error fetching analisis costes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEstadoFilter = (evento) => {
    const nuevoEstado = evento.target.value;
    setEstadoFiltro(nuevoEstado);
    fetchAnalisisCostes(nuevoEstado, areaFiltro);
  };

  const handleAreaFilter = (evento) => {
    const nuevaArea = evento.target.value;
    setAreaFiltro(nuevaArea);
    fetchAnalisisCostes(estadoFiltro, nuevaArea);
  };

  const toggleTypeExpansion = (tipo) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(tipo)) {
      newExpanded.delete(tipo);
    } else {
      newExpanded.add(tipo);
    }
    setExpandedTypes(newExpanded);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      useGrouping: true
    }).format(amount);
  };

  const formatHours = (hours) => {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    }).format(hours) + ' h';
  };

  const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined) return '-';
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      useGrouping: true
    }).format(percentage) + '%';
  };

  const getStatusColor = (estado) => {
    const statusColors = {
      'oportunidad_identificada': '#17a2b8',
      'en_preparacion_propuesta': '#ffc107',
      'supervision': '#fd7e14',
      'entregada': '#6f42c1',
      'rechazada': '#dc3545',
      'aprobado': '#198754',
      'planificada': '#0dcaf0',
      'en_curso': '#28a745',
      'bloqueada': '#e83e8c',
      'cancelada': '#6c757d',
      'finalizada': '#20c997'
    };
    return statusColors[estado] || '#6c757d';
  };

  if (loading) return (
    <div className="text-center py-5">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Cargando...</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="alert alert-danger" role="alert">
      {error}
    </div>
  );

  if (!data) return null;

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>📊 Análisis de Costes por Tipo de Iniciativa</h2>
            <small className="text-muted">
              Actualizado: {new Date(data.metadata.fecha_calculo).toLocaleString('es-ES')}
            </small>
          </div>

          {/* Filtros */}
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="mb-0">🔍 Filtros</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <label htmlFor="estadoFilter" className="form-label">Estado de la Iniciativa:</label>
                  <select 
                    id="estadoFilter"
                    className="form-select" 
                    value={estadoFiltro} 
                    onChange={handleEstadoFilter}
                  >
                    <option value="">Todos los estados</option>
                    {data.metadata.estados_disponibles.map(estado => (
                      <option key={estado.value} value={estado.value}>
                        {estado.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label htmlFor="areaFilter" className="form-label">Área Técnica:</label>
                  <select 
                    id="areaFilter"
                    className="form-select" 
                    value={areaFiltro} 
                    onChange={handleAreaFilter}
                  >
                    <option value="">Todas las áreas</option>
                    {data.metadata.areas_disponibles?.map(area => (
                      <option key={area.value} value={area.value}>
                        {area.label}
                      </option>
                    ))}
                  </select>
                </div>
                {(data.filtros_aplicados.estado || data.filtros_aplicados.area_tecnica) && (
                  <div className="col-md-4 d-flex align-items-end">
                    <div className="alert alert-info mb-0 d-flex align-items-center flex-column">
                      <div className="d-flex align-items-center w-100">
                        <i className="fas fa-filter me-2"></i>
                        <span>Filtros aplicados:</span>
                      </div>
                      {data.filtros_aplicados.estado && (
                        <div className="w-100 mt-1">
                          <small>Estado: <strong>{data.metadata.estados_disponibles.find(e => e.value === data.filtros_aplicados.estado)?.label}</strong></small>
                        </div>
                      )}
                      {data.filtros_aplicados.area_tecnica && (
                        <div className="w-100">
                          <small>Área: <strong>{data.metadata.areas_disponibles?.find(a => a.value == data.filtros_aplicados.area_tecnica)?.label}</strong></small>
                        </div>
                      )}
                      <button 
                        className="btn btn-sm btn-outline-secondary mt-2"
                        onClick={() => {
                          setEstadoFiltro('');
                          setAreaFiltro('');
                          fetchAnalisisCostes('', '');
                        }}
                      >
                        <i className="fas fa-times"></i> Limpiar filtros
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Análisis por Tipo */}
          {data.tipos_iniciativa.map((tipo) => (
            <div key={tipo.tipo_iniciativa} className="card mb-3 shadow-sm">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <button 
                      className="btn btn-link text-decoration-none p-0"
                      onClick={() => toggleTypeExpansion(tipo.tipo_iniciativa)}
                    >
                      <i className={`fas fa-chevron-${expandedTypes.has(tipo.tipo_iniciativa) ? 'down' : 'right'} me-2`}></i>
                      {tipo.tipo_iniciativa_display} ({tipo.totales.count} iniciativas)
                    </button>
                  </h5>
                </div>
              </div>

              {expandedTypes.has(tipo.tipo_iniciativa) && (
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Iniciativa</th>
                          <th>Estado</th>
                          <th>Área Técnica</th>
                          <th>Negocio</th>
                          <th>% Avance</th>
                          <th>% Avance Esperado</th>
                                                    <th>Desviación en <br/>planificación (%)</th>
                          <th>Dedicaciones (h)</th>
                          <th>Desviación en <br/>dedicaciones</th>
                          <th>Horas Totales <br/> Propuesta</th>
                          <th>Coste Real</th>
                          <th>Coste Total Oferta</th>
                          <th>Coste Esperado según Avance</th>
                          <th>% sobre Total Oferta</th>
                          <th>Desviación en coste (€)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tipo.iniciativas.map((iniciativa) => (
                          <tr key={iniciativa.id}>
                            <td>
                              <div>
                                <Link 
                                  to={`/projects/${iniciativa.id}`} 
                                  className="text-decoration-none"
                                >
                                  <strong className="text-primary">{iniciativa.nombre_de_la_oportunidad}</strong>
                                </Link>
                                {iniciativa.descripcion && (
                                  <small className="d-block text-muted">
                                    {iniciativa.descripcion.substring(0, 100)}
                                    {iniciativa.descripcion.length > 100 && '...'}
                                  </small>
                                )}
                              </div>
                            </td>
                            <td>
                              <span 
                                className="badge"
                                style={{ backgroundColor: getStatusColor(iniciativa.estado) }}
                              >
                                {iniciativa.estado_display}
                              </span>
                            </td>
                            <td>{iniciativa.tecnologia_nombre || '-'}</td>
                            <td>{iniciativa.negocio_estructura_nombre || '-'}</td>
                            <td>
                              {iniciativa.porcentaje_avance !== null && iniciativa.porcentaje_avance !== undefined ? (
                                <div className="d-flex align-items-center">
                                  <div className="progress me-2" style={{ width: '60px', height: '20px' }}>
                                    <div
                                      className={`progress-bar ${
                                        iniciativa.porcentaje_avance >= 80 ? 'bg-success' :
                                        iniciativa.porcentaje_avance >= 50 ? 'bg-warning' : 'bg-danger'
                                      }`}
                                      role="progressbar"
                                      style={{ width: `${Math.min(iniciativa.porcentaje_avance, 100)}%` }}
                                    ></div>
                                  </div>
                                  <small>{iniciativa.porcentaje_avance}%</small>
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            {/* Porcentaje de avance esperado */}
                            <td>
                              {(() => {
                                const avanceEsperado = iniciativa.porcentaje_avance_esperado;
                                if (avanceEsperado !== undefined && avanceEsperado !== null) {
                                  return (
                                    <div className="d-flex align-items-center">
                                      <div className="progress me-2" style={{ width: '60px', height: '20px' }}>
                                        <div
                                          className="progress-bar bg-info"
                                          role="progressbar"
                                          style={{ width: `${Math.min(avanceEsperado, 100)}%` }}
                                        ></div>
                                      </div>
                                      <small>{avanceEsperado.toFixed(1)}%</small>
                                    </div>
                                  );
                                }
                                return '-';
                              })()}
                            </td>
                            {/* Desviación en planificación */}
                            <td>
                              {(() => {
                                const desviacion = iniciativa.desviacion_tiempo;
                                if (desviacion !== undefined && desviacion !== null) {
                                  return (
                                    <span className={desviacion < 0 ? 'text-success' : desviacion > 10 ? 'text-danger' : 'text-warning'}>
                                      {desviacion.toFixed(1)}%
                                    </span>
                                  );
                                }
                                return '-';
                              })()}
                            </td>
                            <td>{formatHours(iniciativa.horas_dedicadas)}</td>
                            {/* Desviación en dedicaciones */}
                            <td>
                              {(() => {
                                const horasDedicadas = iniciativa.horas_dedicadas;
                                const porcentajeAvance = iniciativa.porcentaje_avance;
                                const horasTotales = iniciativa.horas_desarrollo_estimado;
                                
                                if (horasDedicadas && porcentajeAvance && horasTotales && porcentajeAvance > 0) {
                                  const horasEsperadas = (porcentajeAvance / 100) * horasTotales;
                                  const desviacion = (horasDedicadas / horasEsperadas) * 100;
                                  const colorClass = desviacion > 120 ? 'text-danger' : desviacion < 80 ? 'text-warning' : 'text-success';
                                  return (
                                    <span className={colorClass}>
                                      {formatPercentage(desviacion)}
                                    </span>
                                  );
                                }
                                return '-';
                              })()
                            }</td>
                            {/* Horas totales propuesta */}
                            <td>
                              {(() => {
                                const horasTotales = iniciativa.horas_desarrollo_estimado;
                                if (horasTotales !== undefined && horasTotales !== null) {
                                  return (
                                    <span className="text-primary">
                                      {formatHours(horasTotales)}
                                    </span>
                                  );
                                }
                                return '-';
                              })()}
                            </td>
                            <td>
                              <strong className="text-success">
                                {formatCurrency(iniciativa.euros_dedicacion)}
                              </strong>
                            </td>
                            <td>
                              <strong className="text-warning">
                                {formatCurrency(iniciativa.coste_estimado_desarrollo)}
                              </strong>
                            </td>
                            {/* Coste esperado según avance */}
                            <td>
                              {(() => {
                                const costeEsperado = iniciativa.coste_esperado;
                                if (costeEsperado !== undefined && costeEsperado !== null) {
                                  return (
                                    <strong className="text-info">
                                      {formatCurrency(costeEsperado)}
                                    </strong>
                                  );
                                }
                                return '-';
                              })()}
                            </td>
                            <td>
                              {iniciativa.porcentaje_sobre_estimado !== null ? (
                                <span className={iniciativa.porcentaje_sobre_estimado > 100 ? 'text-danger' : 'text-success'}>
                                  {formatPercentage(iniciativa.porcentaje_sobre_estimado)}
                                </span>
                              ) : '-'}
                            </td>
                            {/* Desviación en coste */}
                            <td>
                              {(() => {
                                const desviacion = iniciativa.desviacion_coste;
                                if (desviacion !== undefined && desviacion !== null) {
                                  return (
                                    <span className={desviacion > 0 ? 'text-danger' : desviacion < 0 ? 'text-success' : ''}>
                                      {formatCurrency(desviacion)}
                                    </span>
                                  );
                                }
                                return '-';
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}

        </div>
      </div>
    </div>
  );
};

export default AnalisisCostes;